import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./use-auth";
import { toast } from "./use-toast";

interface CreateCostCenterData {
  name: string;
  code?: string;
  description?: string;
  company_id: string;
}

interface UpdateCostCenterData {
  id: string;
  name?: string;
  code?: string;
  description?: string;
  status?: string;
}

export function useCreateCostCenter() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (data: Omit<CreateCostCenterData, 'company_id'> & { company_id: string }) => {
      const { data: result, error } = await supabase
        .from("cost_centers")
        .insert({
          ...data,
          created_by: user?.id,
        })
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cost-centers"] });
      toast({
        title: "Centro de custo criado",
        description: "O centro de custo foi criado com sucesso.",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro ao criar centro de custo",
        description: "Não foi possível criar o centro de custo. Tente novamente.",
        variant: "destructive",
      });
      console.error("Error creating cost center:", error);
    },
  });
}

export function useUpdateCostCenter() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (data: UpdateCostCenterData) => {
      const { id, ...updateData } = data;
      const { data: result, error } = await supabase
        .from("cost_centers")
        .update({
          ...updateData,
          updated_by: user?.id,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cost-centers"] });
      toast({
        title: "Centro de custo atualizado",
        description: "O centro de custo foi atualizado com sucesso.",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro ao atualizar centro de custo",
        description: "Não foi possível atualizar o centro de custo. Tente novamente.",
        variant: "destructive",
      });
      console.error("Error updating cost center:", error);
    },
  });
}

export function useDeleteCostCenter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("cost_centers")
        .update({ status: "inactive" })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cost-centers"] });
      toast({
        title: "Centro de custo removido",
        description: "O centro de custo foi removido com sucesso.",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro ao remover centro de custo",
        description: "Não foi possível remover o centro de custo. Tente novamente.",
        variant: "destructive",
      });
      console.error("Error deleting cost center:", error);
    },
  });
}